import { ICartItem } from '../_store/types'
import { IProduct, IProductVariant, ICategory } from '../_types'

interface IPurchase {
  total_price: number
  delivery_price: number
  external_order_id: string
  promocode: string
  products: any[]
}

export default function useGTM() {
  const safePush = (data: any) => {
    if (typeof window === 'undefined') return
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(data)
  }

  const addItem = ({
    data,
    quantity,
  }: {
    data?: IProduct | IProductVariant
    quantity?: number
  }) => {
    safePush({
      event: 'add_to_cart',
      ecommerce: {
        items: [
          {
            item_name: data?.title?.ru,
            item_id: data?.id,
            price: data?.out_price,
            item_category: data?.categories?.[0]?.title?.ru,
            index: data?.order,
            quantity,
          },
        ],
      },
    })
  }

  const selectItem = ({
    data,
    name,
  }: {
    data?: IProduct | IProductVariant
    name?: string
  }) => {
    safePush({
      event: 'select_item',
      ecommerce: {
        items: [
          {
            item_name: data?.title?.ru,
            item_id: data?.id,
            item_brand: name,
            item_category: data?.slug,
            index: data?.order,
            price: data?.out_price,
            quantity: 1,
          },
        ],
      },
    })
  }

  const addToCartAds = ({ data }: { data?: IProduct | IProductVariant }) => {
    safePush({
      event: 'add_to_cart_ads',
      value: data?.out_price || 0,
      items: [{ id: data?.id, google_business_vertical: 'retail' }],
    })
  }

  const addToCartFb = ({
    data,
    currency,
  }: {
    data?: IProduct | IProductVariant
    currency?: string
  }) => {
    safePush({
      event: 'add_to_cart_fb',
      value: data?.out_price || 0,
      content_ids: [data?.id],
      content_type: 'product',
      currency: currency || 'UZS',
    })
  }

  const removeItem = ({
    cart,
    name,
  }: {
    cart?: ICartItem[]
    name?: string
  }) => {
    safePush({
      event: 'remove_from_cart',
      ecommerce: {
        items:
          cart?.map((el, idx) => ({
            item_brand: name,
            item_name: el.product_name,
            item_category: el.category_name,
            item_id: el.product_id,
            quantity: el.quantity,
            price: el.price,
            index: idx,
          })) || [],
      },
    })
  }

  const beginCheckout = ({ cart }: { cart?: ICartItem[] }) => {
    safePush({
      event: 'begin_checkout',
      ecommerce: {
        items: cart?.map((item, idx) => ({
          item_name: item?.product_name,
          item_id: item?.product_id,
          price: item?.price_with_discount,
          item_category: item?.category_name,
          item_list_name: 'Cart',
          item_list_id: 'CH123',
          index: idx,
          quantity: item?.quantity,
        })),
      },
    })
  }

  const purchase = ({
    data,
    currency,
  }: {
    data?: IPurchase
    currency?: string
  }) => {
    safePush({
      event: 'purchase',
      ecommerce: {
        currency: currency || 'UZS',
        affiliation: 'Online Store',
        value: data?.total_price,
        coupon: data?.promocode,
        tax: '0.00',
        transaction_id: data?.external_order_id,
        shipping: data?.delivery_price,
        items: data?.products?.map((el) => ({
          item_name: el.product_name,
          item_id: el.product_id,
          price: el.price,
          quantity: el.quantity,
        })),
      },
    })
  }

  const viewItemList = ({
    products,
    category,
    shipperName,
    currency = 'UZS',
  }: {
    products: IProduct[]
    category?: ICategory
    shipperName?: string
    currency?: string
  }) => {
    if (!products?.length) return
    safePush({ ecommerce: null })

    safePush({
      event: 'view_item_list',
      ecommerce: {
        items: products.map((item) => ({
          item_name: item?.title?.ru,
          item_id: item?.id,
          price: item?.out_price,
          item_brand: shipperName,
          item_category: category?.title?.ru,
          index: item?.order,
          quantity: 1,
        })),
      },
    })

    const allPrice = products.reduce((sum, el) => sum + (el?.out_price || 0), 0)

    viewItemListAds(products, allPrice)

    // 3️⃣ Facebook Pixel
    viewItemListFb(products, allPrice, currency)
  }

  /** 📣 Google Ads version (view_item_list_ads) */
  const viewItemListAds = (products: IProduct[], total: number) => {
    safePush({
      event: 'view_item_list_ads',
      value: total,
      items: products.map((el) => ({
        id: el.id,
        google_business_vertical: 'retail',
      })),
    })
  }

  /** 📘 Facebook Pixel version (view_item_list_fb) */
  const viewItemListFb = (
    products: IProduct[],
    total: number,
    currency: string
  ) => {
    safePush({
      event: 'view_item_list_fb',
      value: total,
      content_ids: products.map((el) => el.id),
      content_type: 'product',
      currency,
    })
  }

  /** 🚀 Unified sender */
  const sendGTM = (
    key: string,
    {
      product,
      quantity,
      cart,
      order,
      currency,
      name,
      products,
      category,
      shipperName,
    }: {
      product?: IProduct | IProductVariant
      quantity?: number
      cart?: ICartItem[]
      order?: IPurchase
      currency?: string
      name?: string
      products?: IProduct[]
      category?: ICategory
      shipperName?: string
    }
  ) => {
    if (typeof window === 'undefined') return
    window.dataLayer = window.dataLayer || []
    safePush({ ecommerce: null })

    switch (key) {
      case 'add_to_cart':
        addItem({ data: product, quantity })
        break
      case 'select_item':
        selectItem({ data: product, name })
        break
      case 'remove_from_cart':
        removeItem({ cart })
        break
      case 'begin_checkout':
        beginCheckout({ cart })
        break
      case 'purchase':
        purchase({ data: order, currency })
        break
      case 'add_to_cart_ads':
        addToCartAds({ data: product })
        break
      case 'add_to_cart_fb':
        addToCartFb({ data: product, currency })
        break
      case 'view_item_list':
        viewItemList({
          products: products || [],
          category,
          shipperName,
          currency,
        })
        break
      case 'view_item_list_ads':
        viewItemListAds(
          products || [],
          (products || []).reduce((s, p) => s + (p?.out_price || 0), 0)
        )
        break
      case 'view_item_list_fb':
        viewItemListFb(
          products || [],
          (products || []).reduce((s, p) => s + (p?.out_price || 0), 0),
          currency || 'UZS'
        )
        break
      default:
        safePush({ event: key })
        break
    }
  }

  return { sendGTM }
}
